import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { asyncNoop, noop } from 'vet-bones/utils';

import { MAX_COMPETENCY_QUESTIONS } from 'src/constants/limits';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useGenerateQuestions } from 'src/utils/voicebox/hooks/useGenerateQuestions';

export enum CreateProjectStepValue {
  PROJECT_SCOPING = 'PROJECT_SCOPING',
  COMPETENCY_QUESTIONS = 'COMPETENCY_QUESTIONS',
}

export type GeneratedCompetencyQuestion = CompetencyQuestion & {
  isGenerated?: boolean;
};

export type CreateProjectContextValue = {
  currentStep: CreateProjectStepValue;
  name: string;
  description: string;
  competencyQuestions: GeneratedCompetencyQuestion[];
  didGenerateInitialQuestions: boolean;
  isGeneratingQuestions: boolean;
  handleBack: () => void;
  handleGenerateQuestions: () => Promise<void>;
  resetState: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<CreateProjectStepValue>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setDescription: React.Dispatch<React.SetStateAction<string>>;
  setCompetencyQuestions: React.Dispatch<
    React.SetStateAction<GeneratedCompetencyQuestion[]>
  >;
};

export const CreateProjectContext =
  React.createContext<CreateProjectContextValue>({
    currentStep: CreateProjectStepValue.PROJECT_SCOPING,
    name: '',
    description: '',
    competencyQuestions: [],
    didGenerateInitialQuestions: false,
    isGeneratingQuestions: false,
    handleBack: noop,
    handleGenerateQuestions: asyncNoop,
    resetState: noop,
    setCurrentStep: noop,
    setName: noop,
    setDescription: noop,
    setCompetencyQuestions: noop,
  });

export const useCreateProjectState = () => {
  return React.useContext(CreateProjectContext);
};

export const useCreateProjectStateValue = ({
  step: initialStep,
  name: initialName,
  description: initialDescription,
  competencyQuestions: initialCompetencyQuestions,
  didGenerateInitialQuestions: initialDidGenerateInitialQuestions,
  isGeneratingQuestions: initialIsGeneratingQuestions,
}: {
  step?: CreateProjectStepValue;
  name?: string;
  description?: string;
  competencyQuestions?: GeneratedCompetencyQuestion[];
  didGenerateInitialQuestions?: boolean;
  isGeneratingQuestions?: boolean;
} = {}): CreateProjectContextValue => {
  const dispatch = useReduxDispatch();

  const [currentStep, setCurrentStep] = React.useState(
    initialStep || CreateProjectStepValue.PROJECT_SCOPING
  );
  const [name, setNameFn] = React.useState(initialName || '');
  const [description, setDescriptionFn] = React.useState(
    initialDescription || ''
  );
  const [competencyQuestions, setCompetencyQuestionsFn] = React.useState(
    initialCompetencyQuestions || [{ id: uuid(), question: '' }]
  );

  const [didGenerateInitialQuestions, setDidGenerateInitialQuestions] =
    React.useState(initialDidGenerateInitialQuestions ?? false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = React.useState(
    initialIsGeneratingQuestions ?? false
  );

  const setCompetencyQuestions = React.useCallback<
    React.Dispatch<React.SetStateAction<GeneratedCompetencyQuestion[]>>
  >((questionsOrFn) => {
    setCompetencyQuestionsFn((prevQuestions) => {
      const newQuestions =
        typeof questionsOrFn === 'function'
          ? questionsOrFn(prevQuestions)
          : questionsOrFn;
      return newQuestions.slice(0, MAX_COMPETENCY_QUESTIONS);
    });
  }, []);

  const resetState = React.useCallback(() => {
    setNameFn('');
    setDescriptionFn('');
    setCompetencyQuestions([{ id: uuid(), question: '' }]);
    setDidGenerateInitialQuestions(false);
    setIsGeneratingQuestions(false);
  }, [setCompetencyQuestions]);

  const handleBack = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledCreateProjectOverlay({ isOpen: false }));
    resetState();
  }, [dispatch, resetState]);

  const generateQuestions = useGenerateQuestions();

  const handleGenerateQuestions = React.useCallback(async () => {
    if (isGeneratingQuestions) {
      return;
    }

    setIsGeneratingQuestions(true);

    const questions = await generateQuestions(name, description);
    if (questions) {
      setCompetencyQuestions((prevQuestions) => {
        const nextQuestions = prevQuestions.filter(
          (question) => question.question
        );
        const questionsSet = new Set(nextQuestions.map((q) => q.question));
        questions.forEach((question) => {
          if (!questionsSet.has(question.question)) {
            nextQuestions.push({
              id: question.id,
              question: question.question,
              isGenerated: true,
            });
          }
        });
        return nextQuestions;
      });
    }

    setDidGenerateInitialQuestions(true);
    setIsGeneratingQuestions(false);
  }, [
    description,
    generateQuestions,
    isGeneratingQuestions,
    name,
    setCompetencyQuestions,
  ]);

  return {
    currentStep,
    name,
    description,
    competencyQuestions,
    didGenerateInitialQuestions,
    isGeneratingQuestions,
    handleBack,
    handleGenerateQuestions,
    resetState,
    setCurrentStep,
    setName: setNameFn,
    setDescription: setDescriptionFn,
    setCompetencyQuestions,
  };
};
